package dev.danko.roblox_craft_ai

import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import org.jetbrains.compose.web.css.*

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    // This site does not need scrolling itself, but this is a good demonstration for how you might enable this in your
    // own site. Note that we only enable smooth scrolling unless the user has requested reduced motion, which is
    // considered a best practice.
    ctx.stylesheet.registerStyle("html") {
        cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("no-preference"))) {
            Modifier.scrollBehavior(ScrollBehavior.Smooth)
        }
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily(
                "-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Oxygen", "Ubuntu",
                "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "sans-serif"
            )
            .fontSize(18.px)
            .lineHeight(1.5)
    }

    // Silk dividers only extend 90% by default; we want full width dividers in our site
    ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
        Modifier.fillMaxWidth()
    }

    try {
        if (kotlinx.browser.document.getElementById("rc-base-css") == null) {
            val style = kotlinx.browser.document.createElement("style")
            style.id = "rc-base-css"
            // Visible keyboard focus everywhere, small entrance animations, and respect for "reduce motion".
            style.textContent = """
                :focus-visible { outline: 2px solid #9B7BFF; outline-offset: 2px; }
                input:focus-visible, textarea:focus-visible { outline-offset: 0; }
                @keyframes rc-slide-in { from { transform: translateX(32px); opacity: 0; } to { transform: none; opacity: 1; } }
                @keyframes rc-fade-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
                @keyframes rc-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
                .rc-slide-in { animation: rc-slide-in 200ms ease-out; }
                .rc-fade-in { animation: rc-fade-in 140ms ease-out; }
                .rc-backdrop-in { animation: rc-backdrop-in 160ms ease-out; }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; scroll-behavior: auto !important; }
                }
            """.trimIndent()
            kotlinx.browser.document.head?.appendChild(style)
        }
        if (kotlinx.browser.document.getElementById("prism-tomorrow-css") == null) {
            val link = kotlinx.browser.document.createElement("link") as org.w3c.dom.HTMLLinkElement
            link.id = "prism-tomorrow-css"
            link.rel = "stylesheet"
            link.href = "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism-tomorrow.min.css"
            kotlinx.browser.document.head?.appendChild(link)
        }
        if (kotlinx.browser.document.getElementById("prism-core-js") == null) {
            val scriptCore = kotlinx.browser.document.createElement("script") as org.w3c.dom.HTMLScriptElement
            scriptCore.id = "prism-core-js"
            scriptCore.src = "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/prism.min.js"
            // The Lua grammar extends the global Prism object, so it may only load after the core has run.
            scriptCore.onload = {
                val scriptLua = kotlinx.browser.document.createElement("script") as org.w3c.dom.HTMLScriptElement
                scriptLua.id = "prism-lua-js"
                scriptLua.src = "https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/prism-lua.min.js"
                kotlinx.browser.document.head?.appendChild(scriptLua)
            }
            kotlinx.browser.document.head?.appendChild(scriptCore)
        }
    } catch (_: Throwable) {
    }
}

val HeadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(3.cssRem)
        .textAlign(TextAlign.Start)
        .lineHeight(1.2) //1.5x doesn't look as good on very large text
}

val SubheadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .textAlign(TextAlign.Start)
        .color(colorMode.toPalette().color.toRgb().copyf(alpha = 0.8f))
}

val CircleButtonVariant = ButtonStyle.addVariantBase {
    Modifier.padding(0.px).borderRadius(50.percent)
}

val UncoloredButtonVariant = ButtonStyle.addVariantBase {
    Modifier.setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
}
